// src/components/AboutAuthorModal.jsx
// Phase P3: Reader-facing "About the Author" modal with Read Aloud functionality
import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { User, BookOpen, Volume2, Square, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL as backendUrl } from '@/config/api';

const AboutAuthorModal = ({ isOpen, onClose, authorId, authorType = 'managed', onSelectBook }) => {
  const [author, setAuthor] = useState(null);
  const [titles, setTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Read Aloud state
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const audioRef = useRef(null);
  
  // backendUrl imported from centralized config as BACKEND_URL

  // Handle close - stop audio and close modal
  const handleClose = () => {
    // Stop any playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setGeneratingAudio(false);
    // Close the modal
    onClose();
  };

  useEffect(() => {
    if (isOpen && authorId) {
      fetchAuthorData();
      // Reset audio state when modal opens with new author
      setAudioUrl(null);
      setIsPlaying(false);
    } else if (isOpen && !authorId) {
      // No author ID provided - don't leave loading forever
      console.warn('[AboutAuthorModal] Modal opened without valid authorId');
      setLoading(false);
      setAuthor(null);
    }
    
    // Cleanup audio on unmount or close
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [isOpen, authorId, authorType]);

  // Read Aloud handler
  const handleReadAloud = async () => {
    if (!author?.bio) {
      toast.error('No bio available to read');
      return;
    }
    
    // If already playing, stop
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      return;
    }
    
    // If we have cached audio, just play it
    if (audioUrl) {
      playAudio();
      return;
    }
    
    // Generate new audio - use public endpoint for reader-facing modal
    setGeneratingAudio(true);
    
    try {
      const response = await fetch(`${backendUrl}/api/public/managed-authors/${authorId}/bio-tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        // Backend returns audio stream directly (like description TTS)
        const audioBlob = await response.blob();
        const blobUrl = URL.createObjectURL(audioBlob);
        setAudioUrl(blobUrl);
        
        // Play after short delay to let state update
        setTimeout(() => playAudio(blobUrl), 100);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate audio');
      }
    } catch (error) {
      console.error('Error generating bio audio:', error);
      toast.error('Failed to generate audio');
    } finally {
      setGeneratingAudio(false);
    }
  };
  
  const playAudio = (url = audioUrl) => {
    if (!url) return;
    
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.onended = () => setIsPlaying(false);
      audioRef.current.onerror = () => {
        setIsPlaying(false);
        toast.error('Error playing audio');
      };
    }
    
    audioRef.current.src = url;
    audioRef.current.play()
      .then(() => setIsPlaying(true))
      .catch(err => {
        console.error('Error playing audio:', err);
        toast.error('Failed to play audio');
      });
  };

  const fetchAuthorData = async () => {
    console.log('[AboutAuthorModal] Fetching author data...', { authorId, authorType });
    setLoading(true);
    try {
      // Determine endpoint based on author type
      const authorEndpoint = authorType === 'managed' 
        ? `/api/public/managed-authors/${authorId}`
        : `/api/public/authors/by-user/${authorId}`;
      
      const titlesEndpoint = authorType === 'managed'
        ? `/api/public/managed-authors/${authorId}/titles`
        : null;

      console.log('[AboutAuthorModal] Using endpoint:', authorEndpoint, 'authorType:', authorType);

      // Fetch author data
      const authorRes = await fetch(`${backendUrl}${authorEndpoint}`);
      console.log('[AboutAuthorModal] Response status:', authorRes.status);
      if (authorRes.ok) {
        const authorData = await authorRes.json();
        console.log('[AboutAuthorModal] Author data received:', authorData);
        setAuthor(authorData);
        
        // For independent authors, titles are included in the response
        if (authorType === 'independent' && authorData.titles) {
          setTitles(authorData.titles);
        }
      } else {
        console.error('[AboutAuthorModal] Failed to fetch author:', await authorRes.text());
      }

      // Fetch titles separately for managed authors
      if (titlesEndpoint) {
        const titlesRes = await fetch(`${backendUrl}${titlesEndpoint}`);
        if (titlesRes.ok) {
          const titlesData = await titlesRes.json();
          setTitles(titlesData.titles || []);
        }
      }
    } catch (error) {
      console.error('Error fetching author data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = () => {
    // Handle both managed author (image_url) and independent author (headshot_image_url)
    if (author?.image_url) {
      return `${backendUrl}${author.image_url}?t=${Date.now()}`;
    }
    if (author?.headshot_image_url) {
      return `${backendUrl}${author.headshot_image_url}?t=${Date.now()}`;
    }
    if (author?.fallback_logo_url) {
      return `${backendUrl}${author.fallback_logo_url}?t=${Date.now()}`;
    }
    if (author?.logo_image_url) {
      return `${backendUrl}${author.logo_image_url}?t=${Date.now()}`;
    }
    return null;
  };

  // Get bio - handle both managed author (bio) and independent author (long_bio)
  const getBio = () => {
    return author?.bio || author?.long_bio || author?.short_bio || '';
  };

  const getCoverUrl = (title) => {
    // Handle direct cover_image_url (already includes /api/covers/...)
    if (title.cover_image_url) {
      // If it starts with /api, prepend backend URL
      if (title.cover_image_url.startsWith('/api')) {
        return `${backendUrl}${title.cover_image_url}?t=${Date.now()}`;
      }
      return `${backendUrl}${title.cover_image_url}?t=${Date.now()}`;
    }
    // Fallback: construct URL from manuscript_id or id
    const titleId = title.manuscript_id || title.id;
    if (titleId) {
      return `${backendUrl}/api/covers/${titleId}?t=${Date.now()}`;
    }
    return null;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700 text-slate-50">
        {/* NewAudia Logo */}
        <div className="flex justify-center pt-2 pb-1">
          <img 
            src="/newaudia-logo.jpg" 
            alt="NewAudia" 
            className="h-10 w-auto"
          />
        </div>
        
        <DialogHeader>
          <DialogTitle className="flex items-center justify-center gap-2 text-xl">
            <User className="w-5 h-5 text-indigo-400" />
            About the Author
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-t-indigo-500 border-r-transparent border-b-indigo-500 border-l-transparent"></div>
          </div>
        ) : !author ? (
          <div className="text-center py-8">
            <p className="text-slate-400">Author information not available.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Author Info */}
            <div className="flex items-start gap-5">
              {/* Image */}
              {getImageUrl() ? (
                <img
                  src={getImageUrl()}
                  alt={author.pen_name}
                  className="w-24 h-24 rounded-xl object-cover border border-slate-600 flex-shrink-0"
                />
              ) : (
                <div className="w-24 h-24 rounded-xl bg-slate-800 border border-slate-600 flex items-center justify-center flex-shrink-0">
                  <User className="w-10 h-10 text-slate-500" />
                </div>
              )}
              
              {/* Name */}
              <div>
                <h2 className="text-2xl font-bold text-slate-100">{author.pen_name}</h2>
              </div>
            </div>

            {/* Bio */}
            {getBio() && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-400">Biography</span>
                  {/* Read Aloud Button */}
                  <button
                    type="button"
                    onClick={handleReadAloud}
                    disabled={generatingAudio}
                    className="inline-flex items-center gap-2 rounded-full bg-slate-700 hover:bg-slate-600 px-3 py-1.5 text-xs font-medium text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    title={isPlaying ? "Stop" : "Read Aloud"}
                  >
                    {generatingAudio ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Generating...
                      </>
                    ) : isPlaying ? (
                      <>
                        <Square className="w-3.5 h-3.5" />
                        Stop
                      </>
                    ) : (
                      <>
                        <Volume2 className="w-3.5 h-3.5" />
                        Read Aloud
                      </>
                    )}
                  </button>
                </div>
                <div className="prose prose-invert prose-slate max-w-none">
                  <div className="text-slate-300 leading-relaxed whitespace-pre-wrap text-sm">
                    {getBio()}
                  </div>
                </div>
              </div>
            )}

            {/* Close Button - Below the bio */}
            <div className="flex justify-center pt-4">
              <Button
                onClick={handleClose}
                className="w-full sm:w-auto px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white font-semibold"
              >
                Close
              </Button>
            </div>

            {/* Other Titles */}
            {titles.length > 0 && (
              <div className="pt-4 border-t border-slate-700">
                <div className="flex items-center gap-2 mb-4">
                  <BookOpen className="w-5 h-5 text-indigo-400" />
                  <h3 className="font-semibold text-slate-200">Other Titles by {author.pen_name}</h3>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {titles.map((title, index) => {
                    const coverUrl = getCoverUrl(title);
                    const titleName = title.title_name || title.title;
                    const titleId = title.id || title.manuscript_id;
                    
                    const handleTitleClick = () => {
                      if (onSelectBook) {
                        // Build book object for AboutBookModal
                        const bookData = {
                          id: title.manuscript_id || titleId,
                          title: titleName,
                          subtitle: title.subtitle,
                          author: author.pen_name,
                          cover_url: coverUrl,
                          summary: title.summary || title.short_description,
                          short_description: title.short_description,
                          genre: title.genre,
                          theme: title.theme,
                          publisher_author_id: authorId,
                          managed_author_id: title.managed_author_id,
                          purchase_url: title.purchase_url
                        };
                        // Close this modal and open About Book modal for selected title
                        handleClose();
                        onSelectBook(bookData);
                      }
                    };
                    
                    return (
                      <div
                        key={titleId}
                        onClick={handleTitleClick}
                        className="group relative rounded-lg overflow-hidden border border-slate-700 hover:border-slate-600 transition-colors bg-slate-800/50 cursor-pointer"
                      >
                        {/* Cover */}
                        <div className="aspect-[3/4] bg-slate-800 relative">
                          {coverUrl ? (
                            <img
                              src={coverUrl}
                              alt={titleName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <BookOpen className="w-8 h-8 text-slate-600" />
                            </div>
                          )}
                        </div>
                        
                        {/* Info */}
                        <div className="p-2">
                          <p className="text-xs font-medium text-slate-200 truncate">{titleName}</p>
                          {title.subtitle && (
                            <p className="text-xs text-slate-400 truncate">{title.subtitle}</p>
                          )}
                        </div>

                        {/* Hover overlay - now says "Learn More" */}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity">
                          <span className="flex items-center gap-1 text-sm text-white font-medium">
                            <BookOpen className="w-4 h-4" />
                            Learn More
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AboutAuthorModal;
